import { buildApiUrl } from "../../config.js";
import React, { useState, useEffect, useCallback } from "react";
import { PlusCircle, LucideCircleUser } from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import PaymentHistoryTable from "./PaymentHistoryTable";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  FaDollarSign,
  FaArrowDown,
  FaArrowUp,
  FaWallet,
  FaCalculator,
  FaRupeeSign,
} from "react-icons/fa";
import TransferMoney from "./TransferMoney";
import convertToCr from "../ui/convertToCr";

export default function PaymentSectionWholeClient() {
  const { clientId: paramClientId } = useParams();
  const navigate = useNavigate();
  const [paymentSummary, setPaymentSummary] = useState([]);
  const [clientName, setClientName] = useState("");
  const [paymentSummaryCard, setPaymentSummaryCard] = useState({
    totalEstimateAmount: 0,
    totalPurchaseCost: 0,
    currentBalance: 0,
    paidBalance: 0,
    totalTransferIn: 0,
    totalTransferOut: 0,
    contactPerson: "",
  });
  const [paymentData, setPaymentData] = useState({
    totalQuotationAmount: 0,
    paidAmount: {
      total: 0,
      history: [],
    },
  });
  const [newPayment, setNewPayment] = useState({
    date: "",
    amountPaid: "",
    paymentMode: "",
    remarks: "",
    narration: "",
  });
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(paramClientId || "");
  const [loading, setLoading] = useState(false);
  const pendingAmount =
    paymentData?.totalQuotationAmount - (paymentData?.paidAmount?.total || 0);

  const fetchClients = async () => {
    try {
      const response = await axios.get(
        buildApiUrl("/api/clients"),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Failed to fetch clients.");
    }
  };

  const fetchPaymentDetailsCard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        buildApiUrl(`/api/orderItem/order/client/clientordersummary/${selectedClientId}`),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const clientDetails = response.data.clientDetails;
      setClientName(clientDetails.firmName);
      setPaymentSummaryCard({
        totalEstimateAmount: clientDetails.totalEstimateAmount,
        totalPurchaseCost: clientDetails.totalPurchaseCost,
        currentBalance: clientDetails.currentBalance,
        paidBalance: clientDetails.paidBalance,
        totalTransferIn: clientDetails.totalTransferIn,
        totalTransferOut: clientDetails.totalTransferOut,
        contactPerson: clientDetails.contactPerson,
      });
    } catch (error) {
      console.error("Error fetching payment details:", error);
      toast.error("Failed to fetch payment details.");
    } finally {
      setLoading(false);
    }
  }, [selectedClientId]);

  const fetchPaymentSummary = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        buildApiUrl(`/api/orderItem/order/client/paymentDetailSummary/${selectedClientId}`),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.data.success) {
        setPaymentSummary(response.data.paymentSummary);
      }
    } catch (error) {
      console.error("Error fetching payment summary:", error);
      toast.error("Failed to fetch payment summary.");
    } finally {
      setLoading(false);
    }
  }, [selectedClientId]);

  const fetchPaymentDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        buildApiUrl(`/api/payment/client/payment-details/${selectedClientId}`),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const { paidAmount, totalEstimateAmount } = response.data;
      setPaymentData({
        totalQuotationAmount: totalEstimateAmount,
        paidAmount: paidAmount,
      });
    } catch (error) {
      console.error("Error fetching payment details:", error);
      toast.error("Failed to fetch payment details.");
    } finally {
      setLoading(false);
    }
  }, [selectedClientId]);

  useEffect(() => {
    if (!selectedClientId) return;
    fetchPaymentDetailsCard();
    fetchPaymentSummary();
    fetchPaymentDetails();
  }, [
    selectedClientId,
    fetchPaymentDetailsCard,
    fetchPaymentSummary,
    fetchPaymentDetails,
    navigate,
  ]);

  useEffect(() => {
    fetchClients();
  }, []);

  const addPayment = async () => {
    if (!newPayment.date || !newPayment.amountPaid || !newPayment.paymentMode) {
      toast.error("Please fill out all fields!");
      return;
    }

    const paymentDataToAdd = {
      amountPaid: Number(newPayment.amountPaid),
      date: newPayment.date,
      paymentMode: newPayment.paymentMode,
      remarks: newPayment.remarks,
      narration: newPayment.narration,
    };

    try {
      await axios.post(
        buildApiUrl(`/api/payment/client/payment-details/add-payment/${selectedClientId}`),
        paymentDataToAdd,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      toast.success("Payment added successfully!");
      fetchPaymentDetailsCard();
      fetchPaymentSummary();
      fetchPaymentDetails();

      setNewPayment({
        date: "",
        amountPaid: "",
        paymentMode: "",
        remarks: "",
        narration: "",
      });
    } catch (error) {
      console.error("Error adding payment:", error);
      toast.error("Error adding payment. Please try again.");
    }
  };

  const formatDate = (dateString) => format(new Date(dateString), "dd-MM-yyyy");

  const clientOptions = [
    { value: "", label: "Choose a Client" },
    ...clients.map((client) => ({
      value: client.user,
      label: client.firmName,
    })),
  ];

  return (
    <div className="">
      <div className="max-w-7xl mx-auto space-y-2">
        <div className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <LucideCircleUser className="text-blue-600" size={22} />
          <h1 className="text-blue-600 underline underline-offset-2 mb-1">
            {clientName}
          </h1>
          <span className="mb-1">Payment Statement</span>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <label
              htmlFor="client-select"
              className="text-sm font-medium text-gray-700"
            >
              Select Client
            </label>
            <Select
              id="client-select"
              value={clientOptions.find(
                (option) => option.value === selectedClientId
              )}
              onChange={(selectedOption) =>
                setSelectedClientId(selectedOption ? selectedOption.value : "")
              }
              options={clientOptions}
              className="w-full sm:w-64 text-xs"
              classNamePrefix="react-select"
              styles={{
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
                control: (provided, state) => ({
                  ...provided,
                  minHeight: "28px",
                  height: "28px",
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  height: "28px",
                  padding: "0 4px",
                }),
                input: (provided) => ({
                  ...provided,
                  margin: 0,
                }),
                indicatorsContainer: (provided) => ({
                  ...provided,
                  height: "28px",
                }),
              }}
            />
          </div>
        </div>

        <FinancialSummary paymentSummaryCard={paymentSummaryCard} />

        <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200">
          <h2 className="text-sm font-medium text-gray-700 mb-2">
            Record Payment
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-[repeat(5,_1fr)_max-content] gap-2">
            <input
              type="date"
              value={newPayment.date}
              onChange={(e) =>
                setNewPayment({ ...newPayment, date: e.target.value })
              }
              className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-1 outline-none focus:ring-blue-500 transition-colors"
            />
            <input
              type="number"
              placeholder="Amount"
              value={newPayment.amountPaid}
              onChange={(e) =>
                setNewPayment({ ...newPayment, amountPaid: e.target.value })
              }
              className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-1 outline-none focus:ring-blue-500 transition-colors"
            />
            <input
              type="text"
              placeholder="Remarks"
              value={newPayment.remarks}
              onChange={(e) =>
                setNewPayment({ ...newPayment, remarks: e.target.value })
              }
              className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-1 outline-none focus:ring-blue-500 transition-colors"
            />
            <input
              type="text"
              placeholder="Narration"
              value={newPayment.narration}
              onChange={(e) =>
                setNewPayment({ ...newPayment, narration: e.target.value })
              }
              className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-1 outline-none focus:ring-blue-500 transition-colors"
            />
            <select
              value={newPayment.paymentMode}
              onChange={(e) =>
                setNewPayment({ ...newPayment, paymentMode: e.target.value })
              }
              className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-1 outline-none focus:ring-blue-500 transition-colors"
            >
              <option value="">Payment Method</option>
              <option value="G-pay/PhonePe">G-pay/PhonePe</option>
              <option value="Debit Card">Debit Card</option>
              <option value="Bank">Bank</option>
              <option value="Cash">Cash</option>
              <option value="Other">Other</option>
            </select>
            <button
              onClick={addPayment}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center px-2 py-1 rounded-md text-xs transition-colors duration-200"
            >
              <PlusCircle className="mr-1 h-3 w-3" /> Add
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-200">
          <h2 className="text-sm font-medium text-gray-700 mb-2">
            Payment Timeline
          </h2>
          <div className="h-64">
            {loading ? (
              <div className="flex justify-center items-center h-full text-xs text-gray-500">
                Loading chart...
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={paymentData?.paidAmount?.history || []}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    tick={{ fill: "#6b7280", fontSize: 10 }}
                  />
                  <YAxis tick={{ fill: "#6b7280", fontSize: 10 }} />
                  <Tooltip
                    labelFormatter={formatDate}
                    contentStyle={{
                      background: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      fontSize: "12px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="amountPaid"
                    stroke="#3b82f6"
                    fill="#bfdbfe"
                    strokeWidth="1.5"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <PaymentHistoryTable
          clientId={selectedClientId}
          paymentSummary={paymentSummary}
          fetchPaymentSummary={fetchPaymentSummary}
          clientName={clientName}
        />
      </div>
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
    </div>
  );
}

const FinancialSummary = ({ paymentSummaryCard }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
    <FinancialCard
      title="Total Sold Amt."
      amount={paymentSummaryCard.totalEstimateAmount}
      icon={<FaCalculator className="h-4 w-4 text-blue-600" />}
    />
    <FinancialCard
      title="Total Purchase Amt."
      amount={paymentSummaryCard.totalPurchaseCost}
      icon={<FaWallet className="h-4 w-4 text-purple-600" />}
    />
    <FinancialCard
      title="Amt. Received"
      amount={paymentSummaryCard.paidBalance}
      icon={<FaRupeeSign className="h-4 w-4 text-green-600" />}
    />
    <FinancialCard
      title="Transfer In"
      amount={paymentSummaryCard.totalTransferIn}
      icon={<FaArrowDown className="h-4 w-4 text-green-600" />}
    />
    <FinancialCard
      title="Transfer Out"
      amount={paymentSummaryCard.totalTransferOut}
      icon={<FaArrowUp className="h-4 w-4 text-red-600" />}
    />
    <FinancialCard
      title="Current Bal."
      amount={paymentSummaryCard.currentBalance}
      icon={<FaWallet className="h-4 w-4 text-purple-600" />}
    />
  </div>
);

const FinancialCard = ({ title, amount, icon }) => {
  const formattedAmount =
    typeof amount === "number" && !isNaN(amount) ? amount.toFixed(2) : "0.00";
  const isNegative = parseFloat(formattedAmount) < 0;

  return (
    <div className="bg-white rounded-md shadow-sm p-2 border border-gray-200 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-500">{title}</span>
        <span className="p-1.5 bg-gray-100 rounded-full">{icon}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span
          className={`text-sm font-semibold ${
            isNegative ? "text-green-600" : "text-gray-800"
          }`}
        >
          {convertToCr(Math.abs(parseFloat(formattedAmount)).toFixed(2))}
        </span>
        {isNegative && (
          <span className="text-xs font-medium text-green-500 bg-red-50 px-1.5 py-0.5 rounded-full">
            Adv.
          </span>
        )}
      </div>
    </div>
  );
};
